// The one place a crisis number lives.
//
// Every crisis line the DDD apps show comes from this file. An app picks an
// audience list (lists.js) and gets the table back in the shape it has always
// used, so a changed number or changed hours is one edit here and a redeploy
// of each consuming app.
//
// ⚠️ These are crisis numbers. A wrong number in an emergency is worse than
// no number. Before changing an entry:
//   1. open the service's own website (or the ministry that runs it) and
//      confirm the number, the hours and who the line is for;
//   2. never copy a number from another country, another repo or a directory;
//   3. set `verifiedOn` to the date you checked and `source` to the page you
//      read it on.
// The tests in ../test fail on a missing or malformed date or source, a
// source on a different site from `website`, and an em dash in reader-facing
// text.
//
// Fields, per service:
//   country      key into COUNTRIES
//   name         as the service names itself
//   number       what the reader dials or texts, as they would read it
//   description  one or two sentences: hours, who it's for, other channels
//   website      bare domain (and path) shown to the reader
//   type         'call' | 'text' | 'resource'
//   youthFocused true when the line is for children or young people
//   verifiedOn   ISO date the number and hours were last checked
//   source       the page on the service's own site that shows them
//
// Seven markets: Australia, New Zealand, India, the UK, Ireland, Canada and
// the US. Australian English throughout.

export const COUNTRIES = {
  australia: {
    code: 'AU',
    name: 'Australia',
    flag: '🇦🇺',
    emergency: {
      name: 'Emergency Services',
      number: 'Triple Zero (000)',
      description: 'For life-threatening emergencies',
      verifiedOn: '2026-09-30',
      source: 'https://www.infrastructure.gov.au/media-communications/phone/triple-zero'
    }
  },
  newzealand: {
    code: 'NZ',
    name: 'New Zealand',
    flag: '🇳🇿',
    emergency: {
      name: 'Emergency Services',
      number: '111',
      description: 'For life-threatening emergencies',
      verifiedOn: '2026-09-30',
      source: 'https://www.police.govt.nz/contact-us/111-police-emergency'
    }
  },
  india: {
    code: 'IN',
    name: 'India',
    flag: '🇮🇳',
    emergency: {
      name: 'Emergency Services',
      number: '112',
      description: 'For life-threatening emergencies',
      verifiedOn: '2026-09-30',
      source: 'https://www.mha.gov.in/en/commoncontent/emergency-response-support-system-erss'
    }
  },
  uk: {
    code: 'UK',
    name: 'United Kingdom',
    flag: '🇬🇧',
    emergency: {
      name: 'Emergency Services',
      number: '999 or 112',
      description: 'For life-threatening emergencies',
      verifiedOn: '2026-09-30',
      source: 'https://www.gov.uk/guidance/999-and-112-the-uks-national-emergency-numbers'
    }
  },
  ireland: {
    code: 'IE',
    name: 'Ireland',
    flag: '🇮🇪',
    emergency: {
      name: 'Emergency Services',
      number: '999 or 112',
      description: 'For life-threatening emergencies',
      verifiedOn: '2026-09-30',
      source: 'https://www.gov.ie/en/department-of-the-taoiseach/services/how-to-contact-emergency-services-in-ireland/'
    }
  },
  canada: {
    code: 'CA',
    name: 'Canada',
    flag: '🇨🇦',
    emergency: {
      name: 'Emergency Services',
      number: '911',
      description: 'For life-threatening emergencies',
      verifiedOn: '2026-09-30',
      source: 'https://www.canada.ca/en/public-health/services/mental-health-services/mental-health-get-help.html'
    }
  },
  usa: {
    code: 'US',
    name: 'United States',
    flag: '🇺🇸',
    emergency: {
      name: 'Emergency Services',
      number: '911',
      description: 'For life-threatening emergencies',
      verifiedOn: '2026-09-30',
      source: 'https://www.911.gov/'
    }
  }
};

export const SERVICES = {
  // ── Australia ─────────────────────────────────────────────────────────
  'au-lifeline': {
    country: 'australia',
    name: 'Lifeline',
    number: '13 11 14',
    description: '24/7 crisis support and suicide prevention. You can also text 0477 13 11 14',
    website: 'lifeline.org.au',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.lifeline.org.au/131114'
  },
  'au-beyond-blue': {
    country: 'australia',
    name: 'Beyond Blue',
    number: '1300 22 4636',
    description: '24/7 support for anxiety, depression and suicide prevention',
    website: 'beyondblue.org.au',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.beyondblue.org.au/get-support/talk-to-a-counsellor'
  },
  'au-kids-helpline': {
    country: 'australia',
    name: 'Kids Helpline',
    number: '1800 55 1800',
    description: '24/7 counselling for children, teens and young adults',
    website: 'kidshelpline.com.au',
    type: 'call',
    youthFocused: true,
    verifiedOn: '2026-09-30',
    source: 'https://www.kidshelpline.com.au/about'
  },
  'au-13yarn': {
    country: 'australia',
    name: '13YARN',
    number: '13 92 76',
    description: '24/7 crisis support, answered by Aboriginal and Torres Strait Islander crisis supporters',
    website: '13yarn.org.au',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.13yarn.org.au/'
  },
  'au-suicide-call-back': {
    country: 'australia',
    name: 'Suicide Call Back Service',
    number: '1300 659 467',
    description: '24/7 counselling for people aged 15 and over who are feeling suicidal, bereaved by suicide or worried about someone',
    website: 'suicidecallbackservice.org.au',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.suicidecallbackservice.org.au/about-us/'
  },
  // 1800 650 890 is eheadspace, a clinician by phone, 3pm to 10pm local time
  // (headspace.org.au, 30 September 2026). It is not a 24/7 line, so it
  // never leads a list.
  'au-headspace': {
    country: 'australia',
    name: 'headspace',
    number: '1800 650 890',
    description: 'Phone and online support from youth mental health clinicians, 3pm to 10pm every day (your local time), for young people aged 12 to 25 and their families. Not an emergency service',
    website: 'headspace.org.au',
    type: 'call',
    youthFocused: true,
    verifiedOn: '2026-09-30',
    source: 'https://headspace.org.au/'
  },
  // Lines the coaches (Knowledge Hub) and some apps list for family, domestic
  // and sexual violence, and for particular communities.
  'au-1800respect': {
    country: 'australia',
    name: '1800RESPECT',
    number: '1800 737 732',
    description: '24/7 counselling and support for anyone affected by domestic, family or sexual violence. You can also text 0458 737 732',
    website: '1800respect.org.au',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://1800respect.org.au/'
  },

  // ── New Zealand ───────────────────────────────────────────────────────
  // Lifeline Aotearoa and the Suicide Crisis Helpline have run 7am to
  // midnight since November 2025, so 1737 (24/7) leads every NZ list. Their
  // hours appear only in the news post cited as `source`; the numbers are on
  // lifeline.org.nz/services/lifeline-helpline and
  // lifeline.org.nz/services/suicide-crisis-helpline. 1737.org.nz no longer
  // calls the service "Need to Talk?", only "1737".
  'nz-1737': {
    country: 'newzealand',
    name: '1737',
    number: 'Call or text 1737',
    description: 'Free 24/7 support from trained counsellors and peer support workers',
    website: '1737.org.nz',
    type: 'text',
    verifiedOn: '2026-09-30',
    source: 'https://1737.org.nz/how-1737-works'
  },
  'nz-lifeline': {
    country: 'newzealand',
    name: 'Lifeline Aotearoa',
    number: '0800 543 354',
    description: 'Confidential support, 7am to midnight, seven days a week. You can also text 4357, same hours',
    website: 'lifeline.org.nz',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.lifeline.org.nz/about-us/news/lifeline-aotearoa-reduces-operating-hours-due-to-funding-constraints'
  },
  'nz-suicide-crisis': {
    country: 'newzealand',
    name: 'Suicide Crisis Helpline',
    number: '0508 828 865',
    description: 'Support for people in crisis, 7am to midnight, seven days a week',
    website: 'lifeline.org.nz',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.lifeline.org.nz/about-us/news/lifeline-aotearoa-reduces-operating-hours-due-to-funding-constraints'
  },
  'nz-youthline': {
    country: 'newzealand',
    name: 'Youthline',
    number: '0800 376 633',
    description: '24/7 support for young people. Free text 234',
    website: 'youthline.co.nz',
    type: 'call',
    youthFocused: true,
    verifiedOn: '2026-09-30',
    source: 'https://youthline.co.nz/get-help/helpline/'
  },
  'nz-womens-refuge': {
    country: 'newzealand',
    name: "Women's Refuge Crisisline",
    number: '0800 733 843',
    description: '24/7 family violence crisis line (0800 REFUGE). Calls are put through to your local refuge',
    website: 'womensrefuge.org.nz',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://womensrefuge.org.nz/get-help/'
  },

  // ── India ─────────────────────────────────────────────────────────────
  'in-tele-manas': {
    country: 'india',
    name: 'Tele-MANAS',
    number: '14416',
    description: 'Free 24/7 government mental health helpline in 20 languages. Also 1800-891-4416',
    website: 'telemanas.mohfw.gov.in',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://telemanas.mohfw.gov.in/telemanas-services'
  },
  'in-aasra': {
    country: 'india',
    name: 'AASRA',
    number: '+91-22-2754 6669',
    description: '24/7 suicide prevention and emotional support helpline',
    website: 'aasra.info',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.aasra.info/helpline.html'
  },
  'in-vandrevala': {
    country: 'india',
    name: 'Vandrevala Foundation',
    number: '+91 9999 666 555',
    description: 'Free 24/7 mental health support by call or WhatsApp',
    website: 'vandrevalafoundation.com',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.vandrevalafoundation.com/free-counseling'
  },
  'in-icall': {
    country: 'india',
    name: 'iCall (TISS)',
    number: '9152987821',
    description: 'Psychosocial counselling, Monday to Saturday 8am-9pm',
    website: 'icallhelpline.org',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://icallhelpline.org/'
  },
  // 1098 and 181 are run by the Ministry of Women and Child Development;
  // childlineindia.org returned 503 on 30 September 2026, so both point to
  // the ministry. The ministry page doesn't say adults may call 1098 about a
  // child, so the entry no longer claims it.
  'in-child-helpline': {
    country: 'india',
    name: 'Child Helpline',
    number: '1098',
    description: 'Toll-free 24/7 government helpline for children who need care and protection, run by the Ministry of Women and Child Development',
    website: 'wcd.gov.in',
    type: 'call',
    youthFocused: true,
    verifiedOn: '2026-09-30',
    source: 'https://wcd.gov.in/child/child-helpline'
  },
  'in-women-helpline': {
    country: 'india',
    name: 'Women Helpline',
    number: '181',
    description: 'Toll-free 24-hour helpline for women facing violence, run by the Ministry of Women and Child Development. Not yet available in West Bengal',
    website: 'wcd.gov.in',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://wcd.gov.in/women/help'
  },

  // ── United Kingdom ────────────────────────────────────────────────────
  'uk-samaritans': {
    country: 'uk',
    name: 'Samaritans',
    number: '116 123',
    description: 'Free 24/7 emotional support',
    website: 'samaritans.org',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.samaritans.org/how-we-can-help/contact-samaritan/'
  },
  'uk-shout': {
    country: 'uk',
    name: 'Shout',
    number: 'Text SHOUT to 85258',
    description: 'Free 24/7 crisis support via text',
    website: 'giveusashout.org',
    type: 'text',
    verifiedOn: '2026-09-30',
    source: 'https://giveusashout.org/about-us/faq/'
  },
  'uk-mind-infoline': {
    country: 'uk',
    name: 'Mind Infoline',
    number: '0300 123 3393',
    description: 'Mental health information and help finding services in England and Wales, Monday to Friday 9am-6pm (closed on bank holidays). Not a crisis line',
    website: 'mind.org.uk',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.mind.org.uk/information-support/helplines/'
  },
  'uk-childline': {
    country: 'uk',
    name: 'Childline',
    number: '0800 1111',
    description: 'Free, confidential support for anyone under 19',
    website: 'childline.org.uk',
    type: 'call',
    youthFocused: true,
    verifiedOn: '2026-09-30',
    source: 'https://www.nhs.uk/nhs-services/mental-health-services/where-to-get-urgent-help-for-mental-health/'
  },
  // England: 24/7, all ages, including family and friends calling about
  // someone (england.nhs.uk news, 27 August 2024, "NHS 111 offering crisis
  // mental health support for the first time"). Wales: press 2, 24/7
  // (111.wales.nhs.uk). Scotland: nhs.uk lists "NHS 24 111"; nhs24.scot
  // refused automated checks. Northern Ireland: nhs.uk lists nidirect only.
  // In the UK lists since 1 October 2026, after Papyrus closed.
  'uk-nhs-111': {
    country: 'uk',
    name: 'NHS 111',
    number: '111',
    description: 'Urgent mental health help, 24/7, for all ages, including family worried about someone. In England, call 111 and choose the mental health option; in Wales, press 2. In Scotland, 111 is NHS 24. Northern Ireland has no 111 service',
    website: 'nhs.uk',
    type: 'call',
    verifiedOn: '2026-10-01',
    source: 'https://www.nhs.uk/nhs-services/mental-health-services/where-to-get-urgent-help-for-mental-health/'
  },
  'uk-national-da-helpline': {
    country: 'uk',
    name: 'National Domestic Abuse Helpline',
    number: '0808 2000 247',
    description: 'Freephone 24-hour helpline for women experiencing domestic abuse, run by Refuge',
    website: 'nationaldahelpline.org.uk',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.nationaldahelpline.org.uk/'
  },

  // Papyrus HOPELINE247 (0800 068 4141) is gone: the charity went into
  // administration on 7 September 2026 and "HopeLine24/7 is no longer
  // operating". See retired.js.

  // ── Ireland ───────────────────────────────────────────────────────────
  'ie-samaritans': {
    country: 'ireland',
    name: 'Samaritans',
    number: '116 123',
    description: 'Free 24/7 emotional support',
    website: 'samaritans.org/ireland',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.samaritans.org/ireland/'
  },
  'ie-text-about-it': {
    country: 'ireland',
    name: 'Text About It',
    number: 'Text HELLO to 50808',
    description: 'Free, anonymous 24/7 listening service by text. If 50808 won\'t send (48, An Post and Clear Mobile customers), message HELLO on WhatsApp through textaboutit.ie',
    website: 'textaboutit.ie',
    type: 'text',
    verifiedOn: '2026-09-30',
    source: 'https://www.textaboutit.ie/how-it-works/'
  },
  'ie-pieta': {
    country: 'ireland',
    name: 'Pieta',
    number: '1800 247 247',
    description: '24/7 crisis counselling for suicide and self-harm. You can also text HELP to 51444',
    website: 'pieta.ie',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.pieta.ie/'
  },
  'ie-aware': {
    country: 'ireland',
    name: 'Aware',
    number: '1800 80 48 48',
    description: 'Freephone support line for anxiety, depression and bipolar disorder, for yourself or someone you care about, 10am to 10pm, seven days a week',
    website: 'aware.ie',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.aware.ie/support/support-line/'
  },
  // ISPCC's text line (50101) is missing from childline.ie's own list of
  // services and may not be 24/7, so it is left out until it can be checked.
  'ie-childline': {
    country: 'ireland',
    name: 'ISPCC Childline',
    number: '1800 66 66 66',
    description: 'Free 24/7 listening service for anyone aged 18 or under. Call, or chat online at childline.ie',
    website: 'childline.ie',
    type: 'call',
    youthFocused: true,
    verifiedOn: '2026-09-30',
    source: 'https://www.childline.ie/childlines-24-hour-support-line-and-other-services-explained'
  },
  'ie-womens-aid': {
    country: 'ireland',
    name: "Women's Aid",
    number: '1800 341 900',
    description: 'National freephone domestic abuse helpline, 24 hours a day, seven days a week. Instant message support mornings and evenings',
    website: 'womensaid.ie',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.womensaid.ie/'
  },

  // ── Canada ────────────────────────────────────────────────────────────
  'ca-988': {
    country: 'canada',
    name: '9-8-8 Suicide Crisis Helpline',
    number: 'Call or text 988',
    description: '24/7 bilingual suicide prevention and crisis support',
    website: '988.ca',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://988.ca/get-help/what-to-expect'
  },
  'ca-kids-help-phone': {
    country: 'canada',
    name: 'Kids Help Phone',
    number: '1-800-668-6868',
    description: '24/7 support for young people. Text CONNECT to 686868',
    website: 'kidshelpphone.ca',
    type: 'call',
    youthFocused: true,
    verifiedOn: '2026-09-30',
    source: 'https://kidshelpphone.ca/call/'
  },
  'ca-cmha': {
    country: 'canada',
    name: 'Canadian Mental Health Association',
    number: 'Find local branch',
    description: 'Mental health support and resources',
    website: 'cmha.ca',
    type: 'resource',
    verifiedOn: '2026-09-30',
    source: 'https://cmha.ca/find-help/find-cmha-in-your-area/'
  },
  'ca-hope-for-wellness': {
    country: 'canada',
    name: 'Hope for Wellness Help Line',
    number: '1-855-242-3310',
    description: '24/7 counselling and crisis support for all Indigenous people across Canada, by phone or online chat',
    website: 'hopeforwellness.ca',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.hopeforwellness.ca/'
  },
  'ca-awhl': {
    country: 'canada',
    name: "Assaulted Women's Helpline (Ontario)",
    number: '1-866-863-0511',
    description: '24/7 crisis line for women in Ontario who have experienced any form of abuse, in over 200 languages',
    website: 'awhl.org',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.awhl.org/our-services'
  },

  // ── United States ─────────────────────────────────────────────────────
  'us-988': {
    country: 'usa',
    name: '988 Suicide & Crisis Lifeline',
    number: 'Call or text 988',
    description: 'Free 24/7 crisis support and suicide prevention',
    website: '988lifeline.org',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://988lifeline.org/get-help/'
  },
  'us-crisis-text-line': {
    country: 'usa',
    name: 'Crisis Text Line',
    number: 'Text HOME to 741741',
    description: 'Free 24/7 crisis support via text',
    website: 'crisistextline.org',
    type: 'text',
    verifiedOn: '2026-09-30',
    source: 'https://www.crisistextline.org/'
  },
  // SAMHSA's own page: "No, we do not provide counseling." It refers.
  'us-samhsa': {
    country: 'usa',
    name: 'SAMHSA National Helpline',
    number: '1-800-662-4357',
    description: 'Free, confidential 24/7 treatment referral and information for mental health and substance use, in English and Spanish. A referral line, not counselling',
    website: 'samhsa.gov',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.samhsa.gov/find-help/helplines/national-helpline'
  },
  'us-teen-line': {
    country: 'usa',
    name: 'Teen Line',
    number: '(800) 852-8336',
    description: 'Teens helping teens, 6–10pm Pacific time. Text TEEN to 839863, 6–9pm Pacific',
    website: 'didihirsch.org/teenline',
    type: 'call',
    youthFocused: true,
    verifiedOn: '2026-09-30',
    source: 'https://didihirsch.org/teenline/'
  },
  'us-trevor': {
    country: 'usa',
    name: 'The Trevor Project (LGBTQ+ Youth)',
    number: '1-866-488-7386',
    description: 'Free 24/7 crisis support for LGBTQ+ young people. Text START to 678-678',
    website: 'thetrevorproject.org',
    type: 'call',
    youthFocused: true,
    verifiedOn: '2026-09-30',
    source: 'https://www.thetrevorproject.org/get-help/'
  },
  'us-ndvh': {
    country: 'usa',
    name: 'National Domestic Violence Hotline',
    number: '1-800-799-7233',
    description: 'Free, confidential 24/7 support for anyone affected by domestic violence. You can also text START to 88788',
    website: 'thehotline.org',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.thehotline.org/'
  },
  'us-rainn': {
    country: 'usa',
    name: 'RAINN National Sexual Assault Hotline',
    number: '1-800-656-4673',
    description: 'Free, confidential 24/7 support after sexual violence, in English and Spanish. You can also text HOPE to 64673',
    website: 'rainn.org',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://rainn.org/'
  },
  // Not 24/7: never make it the first line of a list.
  'us-trans-lifeline': {
    country: 'usa',
    name: 'Trans Lifeline',
    number: '1-877-565-8860',
    description: 'Peer support run by trans people for trans and questioning people. Phone only, Monday to Friday, 1pm to 9pm US Eastern time',
    website: 'translifeline.org',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://translifeline.org/hotline/'
  },
  'us-veterans-crisis': {
    country: 'usa',
    name: 'Veterans Crisis Line',
    number: 'Dial 988 then press 1',
    description: '24/7 for veterans, service members and their families, whether or not they are enrolled with the VA. You can also text 838255',
    website: 'veteranscrisisline.net',
    type: 'call',
    verifiedOn: '2026-09-30',
    source: 'https://www.veteranscrisisline.net/'
  }
};

// For a reader outside the seven countries. No numbers: a directory is the
// only honest thing to offer someone whose country we don't hold.
export const DIRECTORIES = [
  {
    id: 'int-findahelpline',
    name: 'Find a helpline',
    description: 'Free directory of verified crisis lines in more than 175 countries',
    website: 'findahelpline.com',
    verifiedOn: '2026-09-30',
    source: 'https://findahelpline.com/'
  },
  {
    id: 'int-befrienders',
    name: 'Befrienders Worldwide',
    description: 'Global network of emotional support centres, with a directory of confidential helplines',
    website: 'befrienders.org',
    verifiedOn: '2026-09-30',
    source: 'https://befrienders.org/'
  }
];
